import type { Rng } from '@/core/rng';
import type { ItemId } from '@/data/items';

/**
 * What is in a chest.
 *
 * Content, not code. A table is a list of possibilities with weights, and the
 * only thing the dungeon knows is which table to roll on — so making floor
 * seven generous is an edit here, not a change to how chests work.
 *
 * Weights are relative within a table and need not add to anything. Counts are
 * inclusive ranges, because "three to five shells" is how the tuning
 * conversation goes.
 */

export interface LootEntry {
  readonly item: ItemId;
  /** Relative to the other entries in the same table. */
  readonly weight: number;
  readonly min: number;
  readonly max: number;
}

export interface LootStack {
  readonly item: ItemId;
  readonly count: number;
}

/**
 * The shallows: materials and bait, worth carrying but never worth the trip on
 * their own. The reason to go down is what is further down.
 */
const SHALLOW: readonly LootEntry[] = [
  { item: 'mat:driftwood', weight: 30, min: 1, max: 3 },
  { item: 'mat:shell', weight: 25, min: 1, max: 3 },
  { item: 'bait:worm', weight: 20, min: 2, max: 5 },
  { item: 'mat:feather', weight: 15, min: 1, max: 2 },
  { item: 'seed:turnip', weight: 10, min: 2, max: 4 },
  { item: 'mat:pearl', weight: 3, min: 1, max: 1 },
];

/**
 * Deeper: the same materials in quantity, and the Iron Sword.
 *
 * The sword existed in `data/weapons.ts` with no way to obtain it, which made
 * the belt's tier rules something only a test had ever exercised. A rare drop
 * several floors down is the right way in — the upgrade is the reward for
 * depth, and the belt already retires the blade it replaces.
 */
const DEEP: readonly LootEntry[] = [
  { item: 'mat:driftwood', weight: 22, min: 2, max: 5 },
  { item: 'mat:shell', weight: 20, min: 2, max: 5 },
  { item: 'mat:pearl', weight: 14, min: 1, max: 2 },
  { item: 'bait:shrimp', weight: 16, min: 2, max: 4 },
  { item: 'mat:feather', weight: 12, min: 2, max: 4 },
  { item: 'ironSword', weight: 6, min: 1, max: 1 },
];

/** Floors 1 to 4 are the shallows; below that is worth the walk. */
export function lootTableFor(depth: number): readonly LootEntry[] {
  return depth >= 5 ? DEEP : SHALLOW;
}

/** Chests hold more the further down they are, up to a sensible ceiling. */
export function rollCountFor(depth: number): number {
  return Math.min(4, 1 + Math.floor(depth / 3));
}

/**
 * Draw from a table.
 *
 * Rolls independently rather than sampling without replacement: two stacks of
 * driftwood is a fine outcome, and a chest that could never repeat itself
 * would quietly make rare things commoner as the table shrinks.
 */
export function rollLoot(rng: Rng, table: readonly LootEntry[], rolls: number): LootStack[] {
  if (table.length === 0 || rolls <= 0) return [];

  const total = table.reduce((sum, entry) => sum + entry.weight, 0);
  if (total <= 0) return [];

  const stacks: LootStack[] = [];

  for (let i = 0; i < rolls; i++) {
    let ticket = rng.next() * total;
    const chosen = table.find((entry) => (ticket -= entry.weight) < 0) ?? table[table.length - 1]!;
    stacks.push({ item: chosen.item, count: rng.int(chosen.min, chosen.max) });
  }

  return stacks;
}
